import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../store/LanguageContext';
import { Shield, ShieldCheck, BarChart3, Target, Settings as Cog } from 'lucide-react';
import { API_URL } from '../lib/api';

// GDPR-compliant consent storage. Versioned key so we can re-prompt if the
// schema or law changes (EU Digital Omnibus 2024). Keep < 50 bytes per entry.
const COOKIE_KEY = 'gvs-cookie-consent-v2';
const CONSENT_VALIDITY_MONTHS = 12;

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  browserSignal?: 'opt-out' | 'ask-consent';
  lang?: string;
}

/** Detect Global Privacy Control (GPC) or Do Not Track (DNT) browser signals.
 *  Required by EU Digital Omnibus 2024 — sites MUST honour browser-wide privacy
 *  signals as a "do not consent" instruction. */
function detectBrowserPrivacySignal(): 'opt-out' | 'ask-consent' {
  if (typeof navigator === 'undefined') return 'ask-consent';
  // Global Privacy Control (modern, EU-approved)
  if ((navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true) return 'opt-out';
  // Do Not Track (legacy fallback)
  const dnt = (navigator as { doNotTrack?: string }).doNotTrack
    ?? (window as unknown as { doNotTrack?: string }).doNotTrack;
  if (dnt === '1' || dnt === 'yes') return 'opt-out';
  return 'ask-consent';
}

async function logConsent(prefs: CookiePreferences) {
  // Best-effort; never block the UI on a failed log.
  try {
    await fetch(`${API_URL}/api/cookie-consent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prefs),
      // Don't include credentials — consent is anonymous before login.
    });
  } catch {
    /* ignore */
  }
}

export default function CookieBanner() {
  const { t, lang } = useLanguage();
  const [decided, setDecided] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [browserSignal, setBrowserSignal] = useState<'opt-out' | 'ask-consent'>('ask-consent');
  const [prefs, setPrefs] = useState<CookiePreferences>({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false,
    timestamp: new Date().toISOString(),
  });

  // On mount: check existing consent (within 12-month validity) and browser signal.
  useEffect(() => {
    const saved = sessionStorage.getItem(COOKIE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as CookiePreferences;
        const ageMs = Date.now() - new Date(parsed.timestamp).getTime();
        const validMs = CONSENT_VALIDITY_MONTHS * 30 * 24 * 60 * 60 * 1000;
        if (ageMs < validMs) {
          setDecided(true);
          return;
        }
      } catch { /* fall through to re-prompt */ }
    }

    const signal = detectBrowserPrivacySignal();
    setBrowserSignal(signal);
    if (signal === 'opt-out') {
      // GPC/DNT honoured — pre-select only necessary; user can still override
      setPrefs(p => ({ ...p, functional: false, analytics: false, marketing: false }));
    }
  }, []);

  const persist = (next: CookiePreferences) => {
    const final: CookiePreferences = {
      ...next,
      timestamp: new Date().toISOString(),
      browserSignal,
      lang,
    };
    sessionStorage.setItem(COOKIE_KEY, JSON.stringify(final));
    void logConsent(final);
    setDecided(true);
  };

  const acceptAll = () => persist({ necessary: true, functional: true, analytics: true, marketing: true, timestamp: '' });
  const onlyNecessary = () => persist({ necessary: true, functional: false, analytics: false, marketing: false, timestamp: '' });
  const saveCustom = () => persist(prefs);

  if (decided) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center pointer-events-none">
      <div className="fixed inset-0 bg-black/30 pointer-events-auto" />

      <div className="relative w-full max-w-3xl mx-4 mb-4 bg-white rounded-2xl shadow-2xl pointer-events-auto animate-slide-up overflow-hidden">
        <div className="bg-[#1a1a1a] px-6 py-3 flex items-center gap-3">
          <div className="w-7 h-7 bg-[#c47a3a] rounded-sm flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-xs">GS</span>
          </div>
          <span className="text-white text-sm font-semibold">Gevelsteenbedrijf.be</span>
          <span className="ml-auto text-gray-400 text-xs">GDPR · EU 2024</span>
        </div>

        <div className="p-6">
          <div className="flex gap-4 items-start mb-4">
            <Shield size={28} className="text-[#c47a3a] flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-[#1a1a1a] mb-1">{t('cookie.title')}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{t('cookie.description')}</p>
              {browserSignal === 'opt-out' && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 inline-block">
                  {t('cookie.gpcDetected')}
                </p>
              )}
            </div>
          </div>

          {showSettings && (
            <div className="mb-4 bg-[#faf7f3] rounded-lg p-4 text-sm space-y-3">
              <CategoryRow
                icon={<ShieldCheck size={14} className="text-green-600" />}
                title={t('cookie.essential.title')}
                desc={t('cookie.essential.desc')}
                checked={true}
                disabled
              />
              <CategoryRow
                icon={<Cog size={14} className="text-purple-600" />}
                title={t('cookie.functional.title')}
                desc={t('cookie.functional.desc')}
                checked={prefs.functional}
                onChange={v => setPrefs(p => ({ ...p, functional: v }))}
              />
              <CategoryRow
                icon={<BarChart3 size={14} className="text-blue-600" />}
                title={t('cookie.analytics.title')}
                desc={t('cookie.analytics.desc')}
                checked={prefs.analytics}
                onChange={v => setPrefs(p => ({ ...p, analytics: v }))}
              />
              <CategoryRow
                icon={<Target size={14} className="text-orange-600" />}
                title={t('cookie.marketing.title')}
                desc={t('cookie.marketing.desc')}
                checked={prefs.marketing}
                onChange={v => setPrefs(p => ({ ...p, marketing: v }))}
              />
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            {showSettings ? (
              <>
                <button
                  onClick={saveCustom}
                  className="flex-1 bg-[#c47a3a] hover:bg-[#b06a2a] text-white font-semibold py-3 px-6 rounded-lg transition-colors text-sm"
                >
                  {t('cookie.savePreferences')}
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-[#c47a3a] hover:text-[#b06a2a] font-medium py-3 px-4 text-sm underline underline-offset-2 transition-colors"
                >
                  {t('cookie.hideDetails')}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={acceptAll}
                  className="flex-1 bg-[#c47a3a] hover:bg-[#b06a2a] text-white font-semibold py-3 px-6 rounded-lg transition-colors text-sm"
                >
                  {t('cookie.acceptAll')}
                </button>
                <button
                  onClick={onlyNecessary}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-[#1a1a1a] font-semibold py-3 px-6 rounded-lg transition-colors text-sm"
                >
                  {t('cookie.essentialOnly')}
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="sm:flex-none text-[#c47a3a] hover:text-[#b06a2a] font-medium py-3 px-4 text-sm underline underline-offset-2 transition-colors"
                >
                  {t('cookie.showDetails')}
                </button>
              </>
            )}
          </div>

          <p className="mt-3 text-xs text-gray-400 text-center">
            {t('cookie.legalNote')}{' '}
            <Link to="/cookiebeleid" className="underline hover:text-[#c47a3a] transition-colors">
              {t('cookie.policyLink')}
            </Link>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}

function CategoryRow({ icon, title, desc, checked, onChange, disabled }: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        onClick={() => !disabled && onChange?.(!checked)}
        disabled={disabled}
        className={`flex-shrink-0 mt-0.5 w-9 h-5 rounded-full transition-colors relative ${
          checked ? 'bg-[#c47a3a]' : 'bg-gray-300'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        aria-pressed={checked}
        aria-label={title}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
      <div className="min-w-0">
        <h4 className="font-semibold text-[#1a1a1a] flex items-center gap-2 text-sm">
          {icon} {title}
          {disabled && <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">required</span>}
        </h4>
        <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
      </div>
    </div>
  );
}
